/* eslint-disable ember/classic-decorator-hooks, ember/no-computed-properties-in-native-classes */
import Controller from '@ember/controller';
import { action, computed, set } from '@ember/object';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { tKey } from 'ember-intl';
import { TYPE_PARITY } from 'frontend-utils/utils/constants/constant-values';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import GeneralListsCtaExpandToggle from 'lumen/components/general/lists/cta-expand-toggle';
import GeneralListsListItemsListItemParityOverview from 'lumen/components/general/lists/list-items/list-item-parity-overview';
import { ISSUE_TYPES } from 'lumen/utils/constants/pi-constant-values';
import { formatUrl } from 'lumen/utils/rates-request';

const ALLOWED_ISSUE_TYPES = [
  ISSUE_TYPES.LOSS_SOLDOUT,
  ISSUE_TYPES.LOSS_ROOMTYPE,
  ISSUE_TYPES.LOSS_PRICE_DIFFERENCE,
  ISSUE_TYPES.LOSS_DIFFERENT_CANCELLATION,
  ISSUE_TYPES.MOBILE,
];

export default class HotelParityOverviewController extends Controller {
  queryParams = [
    // { dateAggregation: { scope: 'controller' } },
    { maxPersons: { scope: 'controller' } },
  ];

  @service ajax;
  @service errorTracking;
  @service('hotel-and-market') hotelAndMarketService;
  @service intl;

  @tracked maxPersons = 0;
  @tracked scheduleReport = false;

  dateAggregation = 'week';

  paritiesBaseratesDict = null;
  paritiesWithLosses = null;
  paritiesWithLossesForMajorOTAs = null;
  paritiesWithLossesForOtherOTAs = null;
  paritiesWithWins = null;

  @computed('hotelAndMarketService.hotel.OTAsParity.[]')
  get availableOtaIds() {
    // No need to add exception for PA, API handles this
    return (this.hotelAndMarketService.hotel?.OTAsParity ?? []).map(
      (ota) => ota.id,
    );
  }

  get categoryMapping() {
    return {
      [ISSUE_TYPES.LOSS_SOLDOUT]: {
        id: 'brand_closed',
        name: tKey('parity.category.brand_closed.name'),
        message: tKey('parity.category.brand_closed.message'),
      },
      [ISSUE_TYPES.LOSS_ROOMTYPE]: {
        id: 'brand_roomtype',
        name: tKey('parity.category.brand_roomtype.name'),
        message: tKey('parity.category.brand_roomtype.message'),
      },
      [ISSUE_TYPES.LOSS_PRICE_DIFFERENCE]: {
        id: 'other',
        name: tKey('parity.category.other.name'),
        message: tKey('parity.category.other.message'),
      },
      [ISSUE_TYPES.LOSS_DIFFERENT_CANCELLATION]: {
        id: 'brand_nonref',
        name: tKey('parity.category.brand_nonref.name'),
        message: tKey('parity.category.brand_nonref.message'),
      },
      [ISSUE_TYPES.MOBILE]: {
        id: 'brand_mobile',
        name: tKey('parity.category.brand_mobile.name'),
        message: tKey('parity.category.brand_mobile.message'),
      },
    };
  }

  get exportChoices() {
    return [
      {
        groupName: this.intl.t('general.export_group.export'),
        options: [
          {
            id: 'defined_report',
            monthly: false,
            label: `${this.intl.t('general.next_days', { count: this.shopDays })} Major OTAs`,
            type: 'major',
            selection: this.shopDays,
          },
          {
            id: 'defined_report',
            monthly: false,
            label: `${this.intl.t('general.next_days', { count: this.shopDays })} Other OTAs`,
            type: 'other',
            selection: this.shopDays,
          },
        ],
      },
      {
        groupName: this.intl.t('reports.schedule_report'),
        options: [
          {
            id: 'schedule_report',
            label: this.intl.t('general.export_options.reports'),
            icon: 'calendar-clock',
          },
        ],
      },
    ];
  }

  get formattedTotalLoss() {
    // Format it as a rate
    return this.hotelAndMarketService.formatRate(this.totalLoss);
  }

  get formattedTotalWin() {
    // Format it as a rate
    return this.hotelAndMarketService.formatRate(this.totalWin);
  }

  get isLoading() {
    return this.isLoadingHistoricalData;
  }

  get isScheduleDisabled() {
    const los = this.hotelAndMarketService.hotel?.defaultLosParity?.[0];
    const message = this.hotelAndMarketService.getReportSchedulingMessage(
      'parity',
      {
        los,
        ota: null,
        membershipType: 0,
        persons: this.maxPersons,
      },
    );
    const disabled = isPresent(message);
    return {
      disabled,
      message,
    };
  }

  // Determine all categories for actual issues
  @computed('paritiesWithLossesForMajorOTAs.@each.issueType')
  get issueCategoriesMajor() {
    // Used set, since the categories have to be distinct. Should be supported by all browsers
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set
    const categorySet = this.paritiesWithLossesForMajorOTAs.reduce(
      (total, parity) => {
        // Add Other category as default if no allowed category is found
        const categoryId = this.getCategoryId(parity);
        total.add(categoryId);
        return total;
      },
      new Set(),
    );
    // Convert set to array
    return [...categorySet];
  }

  get los() {
    return this.hotelAndMarketService.hotel?.defaultLosParity?.[0];
  }

  get lossIssuesPerCategory() {
    if (this.lossIssuesPerCategoryOther) {
      return [
        this.lossIssuesPerCategoryOther,
        ...this.lossIssuesPerCategoryMajor,
      ].sort((categoryA, categoryB) => categoryB.total - categoryA.total);
    }

    return this.lossIssuesPerCategoryMajor.sort(
      (categoryA, categoryB) => categoryB.total - categoryA.total,
    );
  }

  @computed(
    'categoryMapping',
    'issueCategoriesMajor.[]',
    'paritiesBaseratesDict',
    'paritiesWithLossesForMajorOTAs.[]',
    'totalLoss',
  )
  get lossIssuesPerCategoryMajor() {
    // Map the categories to pass to the list items
    const mappedCategories = this.issueCategoriesMajor.reduce(
      (total, category) => {
        total[category] = {
          id: this.categoryMapping[category].id,
          name: this.intl.t(this.categoryMapping[category].name).toString(),
          message: this.intl
            .t(this.categoryMapping[category].message)
            .toString(),
          issues: [],
          otas: new Set(),
          total: 0,
          listItemComponent: GeneralListsListItemsListItemParityOverview,
          expandActionComponent: GeneralListsCtaExpandToggle,
          categoryTotal: this.totalLoss,
          type: 'loss',
          paritiesBaseratesDict: this.paritiesBaseratesDict,
          eventPrefix: `hotel-parity-overview-loss-${this.categoryMapping[category].id}`,
        };
        return total;
      },
      {},
    );

    // Loop over all parities that are issues and update the category of each
    this.paritiesWithLossesForMajorOTAs.forEach((parity) => {
      const category = mappedCategories[this.getCategoryId(parity)];
      this.errorTracking.addBreadcrumb({
        data: {
          parity,
          mappedCategories,
          categoryId: this.getCategoryId(parity),
          category,
        },
      });
      category.issues.push(parity);
      category.otas.add(parity.ota);
      category.total += Math.abs(parity.differenceWithBaserate);
    });

    // Make the object an array and we need to make the set an array
    return Object.keys(mappedCategories).map((categoryKey) => {
      mappedCategories[categoryKey].otas = [
        ...mappedCategories[categoryKey].otas,
      ];
      return mappedCategories[categoryKey];
    });
  }

  @computed(
    'paritiesBaseratesDict',
    'paritiesWithLossesForOtherOTAs.length',
    'totalLoss',
  )
  get lossIssuesPerCategoryOther() {
    if (this.paritiesWithLossesForOtherOTAs?.length) {
      // Determine all distinct otas through a set
      const otas = new Set();
      let total = 0;
      this.paritiesWithLossesForOtherOTAs.forEach((parity) => {
        otas.add(parity.ota);
        total += Math.abs(parity.differenceWithBaserate);
      });

      return {
        id: 'wholesalers',
        name: this.intl.t('parity.category.wholesalers.name').toString(),
        message: this.intl.t('parity.category.wholesalers.message').toString(),
        issues: this.paritiesWithLossesForOtherOTAs,
        otas: [...otas],
        total,
        listItemComponent: GeneralListsListItemsListItemParityOverview,
        expandActionComponent: GeneralListsCtaExpandToggle,
        categoryTotal: this.totalLoss,
        type: 'loss',
        paritiesBaseratesDict: this.paritiesBaseratesDict,
        eventPrefix: 'hotel-parity-overview-loss-wholesalers',
      };
    }

    return null;
  }

  @computed('model.parities')
  get parities() {
    return this.model?.parities;
  }

  get shopDays() {
    return this.hotelAndMarketService.hotel?.getParityExportShopDays(
      TYPE_PARITY,
      this.los,
    );
  }

  @computed('paritiesWithLosses.[]')
  get totalLoss() {
    return this.determineTotal('paritiesWithLosses');
  }

  @computed('paritiesWithWins.[]')
  get totalWin() {
    return this.determineTotal('paritiesWithWins');
  }

  @computed('paritiesWithWins.[]', 'totalWin', 'paritiesBaseratesDict')
  get winIssuesPerCategory() {
    if (this.paritiesWithWins?.length) {
      // Determine all distinct otas through a set
      const winOtas = new Set();
      this.paritiesWithWins.forEach((parity) => {
        winOtas.add(parity.ota);
      });

      // Win can only be one category for now
      // It's already in an array so it's a little more future proof
      return [
        {
          id: 'brand_cheaper',
          name: this.intl.t('parity.category.brand_cheaper.name').toString(),
          message: this.intl
            .t('parity.category.brand_cheaper.message')
            .toString(),
          issues: this.paritiesWithWins,
          otas: [...winOtas],
          total: this.totalWin,
          listItemComponent: GeneralListsListItemsListItemParityOverview,
          expandActionComponent: GeneralListsCtaExpandToggle,
          categoryTotal: this.totalWin,
          type: 'win',
          paritiesBaseratesDict: this.paritiesBaseratesDict,
          eventPrefix: 'hotel-parity-overview-win-brand_cheaper',
        },
      ];
    }
    return null;
  }

  init() {
    super.init(...arguments);
    set(this, 'paritiesWithWins', []);
    set(this, 'paritiesWithLosses', []);
    set(this, 'paritiesWithLossesForOtherOTAs', []);
    set(this, 'paritiesWithLossesForMajorOTAs', []);
    set(this, 'paritiesBaseratesDict', {});
    // Added this here since the observer does not fire the first time
    // This forces the arrays to be filled up already
    this.filterParities();
  }

  determineTotal(parityKey) {
    // Calculates the wins over all categories
    return Math.abs(
      (this[parityKey] ?? []).reduce(
        (total, parity) => total + parity.differenceWithBaserate,
        0,
      ),
    );
  }

  /**
   * this functions filters all parities in wins and losses
   * Additionally, it also creates a dict for the baserates. Period ids will be mapped to a single baserate
   */
  filterParities() {
    const otas = this.availableOtaIds;
    const paritiesBaseratesDict = {};
    const paritiesWithWins = [];
    const paritiesWithLosses = [];
    const paritiesWithLossesForOtherOTAs = [];
    const paritiesWithLossesForMajorOTAs = [];

    // Will always return an array, even if the model is still loading
    (this.parities ?? []).forEach((parity) => {
      // Needs to be in parity OTAs
      if (otas.includes(parity.ota?.id)) {
        if (parity.isBaserate) {
          // Don't need to take multiple baserates into account here. We only support this view for hotels with a brand.com matching.
          // This means it should only have a single baserate per period
          paritiesBaseratesDict[parity.period.id] = parity;
        } else if (parity.positionToBaserate > 0) {
          // These are wins
          paritiesWithWins.push(parity);
        } else if (
          parity.positionToBaserate < 0 &&
          parity.positionToBaserate !== -3
        ) {
          // We filter out -3 positionToBaserate because those parities are irrelevant and cause crashes.
          // Most of those -3 issues would point to Brand.com having no data to compare to or similar.
          // Those parities yield no loss/win and have no business here. They also have issueType === 0, which crashes the frontend.
          // These are losses
          paritiesWithLosses.push(parity);

          if (parity.isWholesaleRate) {
            paritiesWithLossesForOtherOTAs.push(parity);
          } else {
            paritiesWithLossesForMajorOTAs.push(parity);
          }
        }
      }
    });

    set(this, 'paritiesBaseratesDict', paritiesBaseratesDict);
    set(this, 'paritiesWithWins', paritiesWithWins);
    set(this, 'paritiesWithLosses', paritiesWithLosses);
    set(this, 'paritiesWithLossesForOtherOTAs', paritiesWithLossesForOtherOTAs);
    set(this, 'paritiesWithLossesForMajorOTAs', paritiesWithLossesForMajorOTAs);
  }

  getCategoryId(parity) {
    return ALLOWED_ISSUE_TYPES.includes(parity.issueType)
      ? parity.issueType
      : ISSUE_TYPES.LOSS_PRICE_DIFFERENCE;
  }

  @action
  closeScheduleModal() {
    this.scheduleReport = false;
  }

  @action
  handleExport(exportChoice) {
    if (exportChoice.id === 'schedule_report') {
      if (!this.isScheduleDisabled?.disabled) {
        set(this, 'scheduleReport', true);
      }
    } else {
      const today = this.hotelAndMarketService.getLocalTime();
      const params = {
        hotel_id: this.hotelAndMarketService.hotel?.id,
        los: this.hotelAndMarketService.hotel?.defaultLosParity?.[0],
        membershiptype: 0,
        type: exportChoice.type,
        from_date_range_start: today.format('YYYY-MM-DD'),
        from_date_range_end: today

          .add(exportChoice.selection, 'day')
          .format('YYYY-MM-DD'),
      };

      if (exportChoice.type !== 'other') {
        params.persons = this.maxPersons;
      }

      const url = formatUrl(
        '/apigateway/v1/app/download/integrities',
        params,
        this.ajax.host,
      );

      openUrl(url);
    }
  }

  @action
  paritiesObserver() {
    // Did this in an observer because I only need one loop to go over all parities
    this.filterParities();
  }
}
